import { defineComponent as _defineComponent } from 'vue'
import { onMounted, onUnmounted, ref } from 'vue';
import { useRouter } from 'vue-router/composables';
import { WorkOrder, WorkOrderAssignedTech } from '@newmoon-org/types';
import { NotificationProgrammatic } from 'buefy';
import { AxiosError } from 'axios';

import {
  listClaimed,
  listUnClaimed,
  claim,
  finalize,
  unclaim,
  joinChannel,
  convert,
} from '@/service/onCallWorkOrder.service';
import { formatDate } from '@/service/dateFormatting.service';

import { useStore } from '@/store';


export default /*#__PURE__*/_defineComponent({
  __name: 'OnCallWorkOrdersListPage',
  setup(__props) {

const store = useStore();
const router = useRouter();
const user = store.getters['auth/user'];
const hasPermission = store.getters['auth/hasPermission'];

const unclaimedWorkOrders = ref<WorkOrder[]>([]);
const claimedWorkOrders = ref<WorkOrder[]>([]);
const channel = joinChannel();

const DEFAULT_LIMIT = 20;
const currentUnClaimedPage = ref<number>(0);
const currentClaimedPage = ref<number>(0);
const totalUnclaimed = ref<number>(0);
const totalClaimed = ref<number>(0);

const isClaiming = ref<boolean>(false);
const isUnclaiming = ref<boolean>(false);
const isLoadingUnclaimed = ref<boolean>(false);
const isLoadingClaimed = ref<boolean>(false);
const isConverting = ref<boolean>(false);
const isFinalizing = ref<boolean>(false);

onUnmounted(() => {
  channel.unsubscribe();
});

onMounted(async () => {
  getUnClaimedWorkOrders({ page: currentUnClaimedPage.value, size: DEFAULT_LIMIT });
  getClaimedWorkOrders({ page: currentClaimedPage.value, size: DEFAULT_LIMIT });

  await Promise.all([
    channel.subscribe(['created', 'converted'], async () => {
      await getUnClaimedWorkOrders({ page: currentUnClaimedPage.value, size: DEFAULT_LIMIT });
    }),
    channel.subscribe(['claimed', 'unclaimed'], async () => {
      await getClaimedWorkOrders({ page: currentClaimedPage.value, size: DEFAULT_LIMIT });
      await getUnClaimedWorkOrders({ page: currentUnClaimedPage.value, size: DEFAULT_LIMIT });
    }),
  ]);
});

function canUnclaim(workOrder: WorkOrder): boolean {
  const employeeId = user.employee.id;
  if (workOrder.assignedTechs.length === 0) return false;
  return workOrder.assignedTechs.some(tech => tech.id === employeeId);
}

async function onPageChangeUnclaimed(page: number) {
  currentUnClaimedPage.value = page;
  await getUnClaimedWorkOrders({ page, size: DEFAULT_LIMIT });
}
async function onPageChangeClaimed(page: number) {
  currentUnClaimedPage.value = page;
  await getClaimedWorkOrders({ page, size: DEFAULT_LIMIT });
}

async function getUnClaimedWorkOrders({ page, size }) {
  try {
    isLoadingUnclaimed.value = true;
    const response = await listUnClaimed({ page, size });
    unclaimedWorkOrders.value = response.content;
    totalUnclaimed.value = response.totalElements;
  } catch (error) {
    console.error('Error fetching unclaimed work orders', error);
    unclaimedWorkOrders.value = [];
    totalUnclaimed.value = 0;
  } finally {
    isLoadingUnclaimed.value = false;
  }
}

async function getClaimedWorkOrders({ page, size }) {
  try {
    isLoadingClaimed.value = true;
    const response = await listClaimed({ page, size });
    claimedWorkOrders.value = response.content;
    totalClaimed.value = response.totalElements;
  } catch (error) {
    console.error('Error fetching claimed work orders', error);
    claimedWorkOrders.value = [];
    totalClaimed.value = 0;
  } finally {
    isLoadingClaimed.value = false;
  }
}

async function goToManage(workOrderId: string) {
  await router.push({ name: 'manage-work-order', params: { workOrderId } });
}

async function claimWorkOrder(workOrderId: string) {
  try {
    isClaiming.value = true;
    await claim(workOrderId, {
      tech: {
        id: user.employee.id,
        firstName: user.employee.firstName,
        lastName: user.employee.lastName,
        email: user.employee.email,
        isPrimary: true,
      },
    });
  } catch (error) {
    console.error('Error claiming work order', error);
    NotificationProgrammatic.open({
      message: (error as AxiosError<{ message: string }>).response?.data?.message ?? 'Error claiming work order',
      type: 'is-danger',
    });
  } finally {
    isClaiming.value = false;
  }
}

async function unClaimWorkOrder(workOrderId: string) {
  try {
    isUnclaiming.value = true;
    await unclaim(workOrderId);
  } catch (error) {
    NotificationProgrammatic.open({
      message: (error as AxiosError<{ message: string }>).response?.data?.message ?? 'Error unclaiming work order',
      type: 'is-danger',
    });
    console.error('Error unclaiming work order', error);
  } finally {
    isUnclaiming.value = false;
  }
}

function getAssignedTech(wo: WorkOrder): WorkOrderAssignedTech {
  return wo.assignedTechs[0];
}

async function convertWorkOrder(id: string) {
  try {
    isConverting.value = true;
    await convert(id);
  } catch (error) {
    NotificationProgrammatic.open({
      message: (error as AxiosError<{ message: string }>).response?.data?.message ?? 'Error unclaiming work order',
      type: 'is-danger',
    });
    console.error('Error converting work order', error);
  } finally {
    isConverting.value = false;
  }
}

function canFinalize(): boolean {
  return hasPermission('on-call-work-order:admin');
}

async function finalizeWorkOrder(id: string): Promise<void> {
  try {
    isFinalizing.value = true;
    await finalize(id);
    await goToManage(id);
  } catch (error) {
    NotificationProgrammatic.open({
      message: (error as AxiosError<{ message: string }>).response?.data?.message ?? 'Error finalizing work order',
      type: 'is-danger',
    });
    console.error('Error finalizing work order', error);
  } finally {
    isFinalizing.value = false;
  }
}

return { __sfc: true,store, router, user, hasPermission, unclaimedWorkOrders, claimedWorkOrders, channel, DEFAULT_LIMIT, currentUnClaimedPage, currentClaimedPage, totalUnclaimed, totalClaimed, isClaiming, isUnclaiming, isLoadingUnclaimed, isLoadingClaimed, isConverting, isFinalizing, canUnclaim, onPageChangeUnclaimed, onPageChangeClaimed, getUnClaimedWorkOrders, getClaimedWorkOrders, goToManage, claimWorkOrder, unClaimWorkOrder, getAssignedTech, convertWorkOrder, canFinalize, finalizeWorkOrder, formatDate }
}

})